// src/components/profileImage.js

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/authContext";

// Helper function to get initials safely
const getInitials = (user) => {
  if (!user) return "U";

  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  const userName = user.userName || "";

  // Try to get initials from first and last name
  if (firstName && lastName) {
    return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
  }
  
  // Fall back to username
  if (userName) {
    return userName.charAt(0).toUpperCase();
  }
  
  if (user.uniqueName) {
    return user.uniqueName.charAt(0).toUpperCase() + user.uniqueName.charAt(1).toUpperCase();
  }

  return "U";
};

// Generate a consistent color based on user data
const getAvatarColor = (user) => {
  const colors = [
    "#3B82F6", // blue-500
    "#EF4444", // red-500
    "#10B981", // emerald-500
    "#F59E0B", // amber-500
    "#8B5CF6", // violet-500
    "#06B6D4", // cyan-500
    "#84CC16", // lime-500
    "#F97316", // orange-500
    "#EC4899", // pink-500
    "#6366F1", // indigo-500
  ];
  
  const key = user?.userName || user?.email || user?.firstName || "default";
  const hash = key.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  return colors[Math.abs(hash) % colors.length];
};

// Avatar fallback component with initials
const InitialsAvatar = ({ user, className, size = "md" }) => {
  const initials = getInitials(user);
  const bgColor = getAvatarColor(user);
  
  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl"
  };
  
  const textSize = sizeClasses[size] || sizeClasses.md;
  
  return (
    <div
      className={`w-full h-full flex items-center justify-center font-semibold text-white ${className}`}
      style={{ backgroundColor: bgColor }}
    >
      <span className={textSize}>{initials}</span>
    </div>
  );
};

// Helper function to normalize image URL and validate it's not an organization logo
const normalizeImageUrl = (url, user = null) => {
  if (!url || typeof url !== 'string') return null;
  
  // Remove double slashes (except after protocol)
  let cleanUrl = url.replace(/([^:]\/)\/+/g, '$1');
  
  // Convert relative URLs to full external URLs
  if (cleanUrl.startsWith('/uploads/')) {
    const originalUrl = cleanUrl;
    cleanUrl = `https://uploadfiles.nashi.sa${cleanUrl}`;
    console.log('🔧 URL Conversion:', {
      original: originalUrl,
      converted: cleanUrl,
      user: user ? {
        userName: user.userName,
        fullName: user.fullName,
        accountType: user.accountType
      } : 'No user data'
    });
  }
  
  // Safeguard: Don't use organization logos for user profiles
  // Check for common organization logo patterns
  const organizationLogoPatterns = [
    /organization.*logo/i,
    /logo.*organization/i,
    /org.*logo/i,
    /team.*logo/i,
    /logo.*team/i
  ];
  
  const isOrganizationLogo = organizationLogoPatterns.some(pattern => 
    pattern.test(cleanUrl)
  );
  
  if (isOrganizationLogo) {
    console.warn('🚨 Detected organization logo being used as profile picture, blocking:', cleanUrl);
    console.warn('🚨 User data:', user ? {
      firstName: user.firstName,
      lastName: user.lastName,
      userName: user.userName,
      accountType: user.accountType
    } : 'No user data');
    return null;
  }
  
  return cleanUrl;
};

const ProfileImage = ({ 
  className = "w-10 h-10 rounded-full overflow-hidden", 
  alt, 
  image = null, 
  user: propUser = null,
  size = "md",
  lazy = true,
}) => {
  const [showImage, setShowImage] = useState(false);
  const { user: currentUser } = useAuth();
  
  // Determine which user data to use
  const user = propUser || currentUser;
  
  // Get and normalize the image URL - pass user data for organization logo detection
  const imageUrl = normalizeImageUrl(image || user?.profilePicture, user);
  
  // Additional safeguard: Check user data for organization-related content
  useEffect(() => {
    if (user && (image || user?.profilePicture)) {
      const originalUrl = image || user?.profilePicture;
      console.log('🖼️ ProfileImage Debug:', {
        user: {
          firstName: user.firstName,
          lastName: user.lastName,
          userName: user.userName,
          accountType: user.accountType
        },
        originalUrl,
        processedUrl: imageUrl,
        hasOrganizationData: !!(user.organization || user.metaData?.organization)
      });
    }
  }, [user, image, imageUrl]);
  
  // Reset when URL changes
  useEffect(() => {
    setShowImage(false);
    
    if (!imageUrl) return;
    
    console.log('🖼️ Testing image URL:', imageUrl);
    
    // Create a new image element to test loading
    const img = new Image();
    
    img.onload = () => {
      console.log('✅ Image loaded successfully:', imageUrl);
      setShowImage(true);
    };
    
    img.onerror = (error) => {
      console.log('❌ Image failed to load:', imageUrl, error);
      setShowImage(false);
    };
    
    // Start loading the image
    img.src = imageUrl;
    
    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  // If we should show the image and have a valid URL
  if (showImage && imageUrl) {
    return (
      <div className={className}>
        <img
          src={imageUrl}
          alt={alt || `${user?.firstName || 'User'}'s profile`}
          className="w-full h-full object-cover rounded-full"
          loading={lazy ? "lazy" : "eager"}
        />
      </div>
    );
  }

  // Otherwise show initials
  return (
    <div className={className}>
      <InitialsAvatar 
        user={user} 
        className="" 
        size={size}
      />
    </div>
  );
};

export default ProfileImage;
